import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function TrigFunctionsCalculator() {
  const [angle, setAngle] = useState("");
  const [unit, setUnit] = useState("degrees");
  const [result, setResult] = useState<{
    sin: number;
    cos: number;
    tan: number;
  } | null>(null);

  const calculate = () => {
    const angleValue = parseFloat(angle);

    if (isNaN(angleValue)) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid angle.",
        variant: "destructive",
      });
      return;
    }

    // Convert to radians if necessary
    const radians = unit === "degrees" ? (angleValue * Math.PI) / 180 : angleValue;

    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    const tan = Math.tan(radians);

    setResult({ sin, cos, tan });

    toast({
      title: "Calculation Complete",
      description: "Trigonometric values calculated",
    });
  };

  const reset = () => {
    setAngle("");
    setUnit("degrees");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Trigonometric Functions Calculator" 
      calculatorPath="/calculator/trig-functions"
      category="math"
      subcategory="Trigonometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Trigonometric Functions Calculator</CardTitle>
            <CardDescription>
              Calculate sin, cos, and tan for any angle
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="angle">Angle</Label>
                <Input
                  id="angle"
                  type="number"
                  step="any"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder="Enter angle"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="degrees">Degrees (°)</SelectItem>
                    <SelectItem value="radians">Radians (rad)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">sin({angle}{unit === "degrees" ? "°" : " rad"})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere flex-1">{result.sin.toFixed(6)}</p>
                      <CopyResultButton value={result.sin.toFixed(6)} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">cos({angle}{unit === "degrees" ? "°" : " rad"})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere flex-1">{result.cos.toFixed(6)}</p>
                      <CopyResultButton value={result.cos.toFixed(6)} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">tan({angle}{unit === "degrees" ? "°" : " rad"})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-3xl font-bold break-all overflow-wrap-anywhere flex-1">
                        {Math.abs(result.tan) > 1000 ? "Undefined" : result.tan.toFixed(6)}
                      </p>
                      <CopyResultButton value={Math.abs(result.tan) > 1000 ? "Undefined" : result.tan.toFixed(6)} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Common Angles</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground grid grid-cols-2 gap-2">
                <p>0° = 0 rad</p>
                <p>30° = π/6 rad</p>
                <p>45° = π/4 rad</p>
                <p>60° = π/3 rad</p>
                <p>90° = π/2 rad</p>
                <p>180° = π rad</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
